import React from 'react';
import { useTranslation } from 'react-i18next';
import AssetDetailSection from './AssetDetailSection';
import InputField from '../../../components/ui/InputField';
import CoordinatesFields from '../../../components/location/CoordinatesFields';

export interface AssetAddress {
    street: string;
    houseNumber: string;
    floor: string;
    postalCode: string;
    city: string;
    district: string;
    geoLat: number | null;
    geoLng: number | null;
}

interface AddressSectionProps {
    address: AssetAddress;
    onChange: (updates: Partial<AssetAddress>) => void;
}

const AddressSection: React.FC<AddressSectionProps> = ({ address, onChange }) => {
    const { t } = useTranslation();

    return (
        <AssetDetailSection title={t('asset_detail.sections.address')}>
            <div className={'flex flex-col gap-4'}>
                <div className={'text-sm text-foreground/80 bg-foreground/5 border border-foreground/10 rounded-lg px-4 py-3'}>
                    {t('asset_detail.fields.address_permanence_warning')}
                </div>
                {/* From `sm` up, all rows share the same outer grid template so
                    field edges align vertically across rows:
                      col 1 (flex)   → right-aligned: Straße, Stadt, Längengrad
                      col 2 (110px)  → left-aligned:  Hausnr., Stadtteil start here
                      col 3 (140px)  → Etage, Stadtteil continues

                    Below `sm` that template does not fit: 110 + 140 + gaps take
                    274px of a ~390px viewport, leaving ~60px for column 1 — the
                    street label truncated to "Str…", and row 2's nested
                    120px+flex grid overlapped inside it. So the template starts
                    at `sm` and the rows stack their own way underneath. */}
                {/* Row 1: street | house no. | floor */}
                <div className={'grid grid-cols-2 gap-3 sm:grid-cols-[minmax(0,1fr)_110px_140px]'}>
                    <InputField
                        className={'col-span-2 sm:col-span-1'}
                        label={`${t('asset_detail.fields.street')} *`}
                        value={address.street}
                        onChange={(val) => onChange({ street: val })}
                    />
                    <InputField
                        label={t('asset_detail.fields.house_number')}
                        value={address.houseNumber}
                        onChange={(val) => onChange({ houseNumber: val })}
                    />
                    <InputField
                        label={t('asset_detail.fields.floor_number')}
                        placeholder={t('asset_detail.fields.floor_placeholder')}
                        value={address.floor}
                        onChange={(val) => onChange({ floor: val })}
                    />
                </div>
                {/* Row 2: PLZ+Stadt nested inside col 1 | Stadtteil spans cols 2–3 */}
                <div className={'grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_110px_140px]'}>
                    <div className={'grid grid-cols-[90px_minmax(0,1fr)] gap-3 sm:grid-cols-[120px_minmax(0,1fr)]'}>
                        <InputField
                            label={`${t('asset_detail.fields.postal_code')} *`}
                            value={address.postalCode}
                            onChange={(val) => onChange({ postalCode: val })}
                        />
                        <InputField
                            label={`${t('asset_detail.fields.city')} *`}
                            value={address.city}
                            onChange={(val) => onChange({ city: val })}
                        />
                    </div>
                    <div className={'sm:col-span-2'}>
                        <InputField
                            label={t('asset_detail.fields.district')}
                            value={address.district}
                            onChange={(val) => onChange({ district: val })}
                        />
                    </div>
                </div>
                <CoordinatesFields
                    value={{ geoLat: address.geoLat, geoLng: address.geoLng }}
                    onChange={(next) => onChange(next)}
                    addressParts={[address.street, address.houseNumber, address.postalCode, address.city]}
                />
            </div>
        </AssetDetailSection>
    );
};

export default AddressSection;
